import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// Static game data (mirrors server constants)
const CLASSES = [
  { value: 'Fighter', label: 'Fighter',  desc: 'Sworn blade of the Accord. Masters of combat. Starts with Hide Breastplate, Hide Cap, Half Spear.',  hp: 20, mp: 0,  att: 5, def: 1 },
  { value: 'Mage',    label: 'Mage',     desc: 'Heir to the elemental seeds first planted at Brightspire. Wielders of arcane magic. Starts with Robes.',  hp: 12, mp: 24, att: 1, def: 4 },
  { value: 'Cleric',  label: 'Cleric',   desc: 'Channel of a chosen god. Divine healer empowered by your patron deity. Starts with Leather Breastplate.', hp: 16, mp: 20, att: 2, def: 3 },
  { value: 'Thief',   label: 'Thief',    desc: 'Whisper of the Antarian alleys. Stealth and cunning. Starts with Leather Breastplate, Leather Cap, Dagger.',   hp: 15, mp: 0,  att: 3, def: 2 },
  { value: 'Psion',   label: 'Psion',    desc: 'Mind-reader from the Late-Thread schools. Master of psionic disciplines. Starts with Robes.',  hp: 14, mp: 22, att: 2, def: 2 },
  { value: 'Druid',   label: 'Druid',    desc: 'Voice of the wild beyond the wall. Nature magic, charming, and summoning. Starts with Leather Breastplate.', hp: 16, mp: 18, att: 2, def: 2 },
];

const RACES = [
  { value: 'Human',    label: 'Human',    bonus: '+3 VIT, +50 gold. Iconic card: Founding Accord (draw + heal + AP).',                                       statBonus: { vitality: 3 } },
  { value: 'Dwarf',    label: 'Dwarf',    bonus: '+2 STR, +2 VIT, Health Potion. Iconic card: Stoneshield Rune (block wall opener).',                            statBonus: { strength: 2, vitality: 2 } },
  { value: 'High Elf', label: 'High Elf', bonus: '+2 MEN, +2 AGL, -1 VIT, Health & Mana Potion. Iconic card: Elven Precision (auto-hit + spell amp).',           statBonus: { mentality: 2, agility: 2, vitality: -1 } },
  { value: 'Drow',     label: 'Drow',     bonus: '+1 STR, +2 AGL, +1 MEN, -1 VIT, +50 gold. Iconic card: Shadowmeld (control + pierce).',                        statBonus: { strength: 1, agility: 2, mentality: 1, vitality: -1 } },
  { value: 'Gnome',    label: 'Gnome',    bonus: '+5 MEN, -1 STR/AGL/VIT, Health & Mana Potion. Iconic card: First Planting (draw 2, scry, +1 AP).',             statBonus: { mentality: 5, strength: -1, agility: -1, vitality: -1 } },
  { value: 'Halfling', label: 'Halfling', bonus: '+2 AGL, +1 VIT, Shortsword. Iconic card: A Story Begun (lucky + permanent +ATT).',                              statBonus: { agility: 2, vitality: 1 } },
  { value: 'Half-Orc', label: 'Half-Orc', bonus: '+3 STR, +1 VIT, -1 AGL, Club. Iconic card: Civic Resolve (block + heal + ATT buff).',                           statBonus: { strength: 3, vitality: 1, agility: -1 } },
];

const ALIGNMENTS = [
  { value: 'LG', label: 'Lawful Good' },
  { value: 'NG', label: 'Neutral Good' },
  { value: 'CG', label: 'Chaotic Good' },
  { value: 'LN', label: 'Lawful Neutral' },
  { value: 'N',  label: 'True Neutral' },
  { value: 'CN', label: 'Chaotic Neutral' },
  { value: 'LE', label: 'Lawful Evil' },
  { value: 'NE', label: 'Neutral Evil' },
  { value: 'CE', label: 'Chaotic Evil' },
];

const ELEMENTAL_SEEDS = [
  { value: 'Acid',        label: 'Acid',        spell: 'Acid Arrow' },
  { value: 'Air',         label: 'Air',         spell: 'Gust' },
  { value: 'Cold',        label: 'Cold',        spell: 'Chill Touch' },
  { value: 'Earth',       label: 'Earth',       spell: 'Tremor' },
  { value: 'Electricity', label: 'Electricity', spell: 'Shocking Grasp' },
  { value: 'Fire',        label: 'Fire',        spell: 'Burning Hands' },
  { value: 'Sonic',       label: 'Sonic',       spell: 'Lesser Sonic Boom' },
  { value: 'Water',       label: 'Water',       spell: 'Trickle' },
];

const DEITIES = [
  { value: 'Thormir', label: 'Thormir',  title: 'God of Healing and Light',      align: 'NG', spell: 'Cure Minor Wounds' },
  { value: 'Calimah', label: 'Calimah',  title: 'God of Evil and Darkness',      align: 'NE', spell: 'Blindness' },
  { value: 'Livella', label: 'Livella',  title: 'Goddess of Order and Healing',  align: 'LN', spell: 'Minor Shield of Law' },
  { value: 'Descrod', label: 'Descrod',  title: 'God of Chaos and Weakness',     align: 'CN', spell: 'Sap' },
  { value: 'Hantine', label: 'Hantine',  title: 'Goddess of Magic and Strength', align: 'N',  spell: 'Virtue' },
];

const DISCIPLINES = [
  { value: 'STR', label: 'Psychometabolism', desc: 'Perfecting the body through the mind',      stat: 'Strength',  spells: 'Inner Strength, Psionic Weakness' },
  { value: 'AGL', label: 'Psychomobility',   desc: 'Your mind increases your own motion',        stat: 'Agility',   spells: 'Agile Mind, Psionic Slowness' },
  { value: 'VIT', label: 'Psychokinesis',    desc: 'Your mind reaches out to move things',       stat: 'Vitality',  spells: 'Vital Destruction, Psychic Health' },
  { value: 'MEN', label: 'Clairsentience',   desc: 'Seeing the unseen, hearing the unheard',     stat: 'Mentality', spells: 'Mind Boost, Mental Crush' },
  { value: 'CHA', label: 'Telepathy',        desc: 'Mental communication and projection',        stat: 'Charisma',  spells: 'Encephalic Charisma, Aura of Awesomeness' },
];

const STAT_DESCRIPTIONS = {
  strength:  'Governs physical attacks and damage',
  agility:   'Governs speed and defense',
  vitality:  'Boosts HP gained each level (hit die + VIT mod)',
  mentality: 'Governs mana per level and magic potency',
  charisma:  'Charm success & shop discounts',
};

const RegisterPage = () => {
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  // Account fields
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [characterName, setCharacterName] = useState('');

  // Character choices
  const [charClass, setCharClass] = useState('');
  const [race, setRace] = useState('');
  const [alignment, setAlignment] = useState('');
  const [seed, setSeed] = useState('');
  const [deity, setDeity] = useState('');
  const [discipline, setDiscipline] = useState('');

  // Stats
  const [stats, setStats] = useState({ strength: 13, agility: 13, vitality: 13, mentality: 13, charisma: 13 });

  // Phase 7 — Code of Conduct acceptance (required for signup)
  const [acceptedCoC, setAcceptedCoC] = useState(false);

  const totalPoints = Object.values(stats).reduce((s, v) => s + v, 0);
  const remaining = 65 - totalPoints;

  const setStat = (key, val) => {
    const v = Math.max(6, Math.min(18, parseInt(val) || 6));
    setStats(prev => ({ ...prev, [key]: v }));
  };

  const raceInfo = useMemo(() => RACES.find(r => r.value === race), [race]);
  const classInfo = useMemo(() => CLASSES.find(c => c.value === charClass), [charClass]);

  const needsSeed       = charClass === 'Mage';
  const needsDeity      = charClass === 'Cleric';
  const needsDiscipline = charClass === 'Psion';

  const canProceedStep1 = username.length >= 3 && email.includes('@') && password.length >= 8 && password === confirmPassword && characterName.length >= 2 && acceptedCoC;
  const canProceedStep2 = charClass && race && alignment
    && (!needsSeed || seed)
    && (!needsDeity || deity)
    && (!needsDiscipline || discipline);
  const canSubmit = canProceedStep1 && canProceedStep2 && totalPoints === 65;

  const onSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);

    const userData = {
      username,
      email,
      password,
      acceptedCoC,
      acceptedCoCVersion: '1.0',
      character: {
        name: characterName,
        class: charClass,
        race,
        alignment,
        stats,
        ...(needsSeed && { seed }),
        ...(needsDeity && { deity }),
        ...(needsDiscipline && { discipline }),
      }
    };

    const result = await registerUser(userData);
    if (result.success) {
      toast.success('Character created! You can now log in.');
      navigate('/login');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-form fade-in" style={{ maxWidth: 560 }}>
        <h2 style={{ textAlign: 'center', color: '#d4af37', marginBottom: 4 }}>Create Your Character</h2>
        <p style={{ textAlign: 'center', color: '#8a8a9a', fontSize: 13, marginBottom: 16 }}>
          Step {step} of 3 {step === 1 ? '- Account' : step === 2 ? '- Class, Race & Alignment' : '- Attributes'}
        </p>

        {/* Progress bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
          {[1, 2, 3].map(s => (
            <div key={s} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: s <= step ? '#d4af37' : '#2a2a3a',
              transition: 'background 0.3s'
            }} />
          ))}
        </div>

        {/* STEP 1: Account */}
        {step === 1 && (
          <div>
            <div style={{
              background: 'linear-gradient(135deg, rgba(212,175,55,0.10), rgba(131,157,191,0.08))',
              border: '1px solid rgba(212,175,55,0.35)',
              borderRadius: 6,
              padding: '12px 14px',
              marginBottom: 16,
              fontSize: 13,
              lineHeight: 1.55,
              color: '#cfd2dc'
            }}>
              <div style={{ color: '#d4af37', fontWeight: 600, marginBottom: 4 }}>Welcome, traveller.</div>
              <p style={{ margin: '0 0 6px 0' }}>
                Castle Antaria sits on a wound. Below it, an old dungeon breathes again, and the bandits on the road outside are only the first of its tells. The King is hiring.
              </p>
              <p style={{ margin: 0, color: '#9aa0b3', fontSize: 12 }}>
                You'll choose a class, race, and alignment. Combat is card-driven — 3 AP per turn, your deck refreshes each fight. Branches matter: the alignment chains route across each other, so the choice you make in dialogue will close some doors and open others.
              </p>
            </div>
            <div className="form-group">
              <label>Username</label>
              <input type="text" value={username} onChange={e => setUsername(e.target.value)} disabled={loading} />
              {username && username.length < 3 && <div className="error-message">Minimum 3 characters</div>}
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} disabled={loading} />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} disabled={loading} />
              {password && password.length < 8 && <div className="error-message">Minimum 8 characters</div>}
            </div>
            <div className="form-group">
              <label>Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} disabled={loading} />
              {confirmPassword && password !== confirmPassword && <div className="error-message">Passwords do not match</div>}
            </div>
            <div className="form-group">
              <label>Character Name</label>
              <input type="text" value={characterName} onChange={e => setCharacterName(e.target.value)} disabled={loading} />
              {characterName && characterName.length < 2 && <div className="error-message">Minimum 2 characters</div>}
            </div>
            <div className="form-group" style={{ marginTop: 4 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13, color: '#ccc' }}>
                <input
                  type="checkbox"
                  checked={acceptedCoC}
                  onChange={e => setAcceptedCoC(e.target.checked)}
                  disabled={loading}
                  style={{ marginTop: 3 }}
                />
                <span>
                  I have read and agree to the{' '}
                  <Link to="/code-of-conduct" target="_blank" rel="noopener noreferrer" style={{ color: '#d4af37' }}>
                    Code of Conduct
                  </Link>.
                </span>
              </label>
            </div>
            <button className="btn btn-primary" disabled={!canProceedStep1} onClick={() => setStep(2)} style={{ width: '100%', marginTop: 8 }}>
              Next
            </button>
            <button
              type="button"
              className="btn"
              disabled={!canProceedStep1 || loading}
              onClick={() => {
                // Quick Start preset: Human Fighter, Neutral Good, balanced stats.
                // Skips the class/race/alignment/stat-allocation choices and
                // submits straight to character creation. Players can always
                // Restart from Step 2 if they want to customize instead.
                setCharClass('Fighter');
                setRace('Human');
                setAlignment('NG');
                setSeed('');
                setDeity('');
                setDiscipline('');
                setStats({ strength: 15, agility: 12, vitality: 14, mentality: 10, charisma: 14 });
                setStep(3);
              }}
              style={{ width: '100%', marginTop: 8, background: '#1e3a2e', borderColor: '#3a5a4a', color: '#a8d8b8', display: 'flex', flexDirection: 'column', gap: 2, padding: '10px 12px' }}
              title="Skip class/race/alignment selection with a balanced Human Fighter preset. Great for first-time players — you can still adjust attributes on the next page."
            >
              <span style={{ fontWeight: 600 }}>⚡ Quick Start — Recommended for Beginners</span>
              <span style={{ fontSize: 11, color: '#7eb8da', fontWeight: 400 }}>
                Human Fighter, Neutral Good · STR 15 · AGL 12 · VIT 14 · MEN 10 · CHA 14
              </span>
            </button>
          </div>
        )}

        {/* STEP 2: Class/Race/Alignment */}
        {step === 2 && (
          <div>
            {/* Class */}
            <div className="form-group">
              <label style={{ color: '#d4af37', fontSize: 14, fontWeight: 600 }}>Choose Your Class</label>
              <div className="rc-card-grid">
                {CLASSES.map(c => (
                  <div
                    key={c.value}
                    className={'rc-card' + (charClass === c.value ? ' rc-card-selected' : '')}
                    onClick={() => { setCharClass(c.value); setSeed(''); setDeity(''); setDiscipline(''); }}
                  >
                    <div className="rc-card-name">{c.label}</div>
                    <div className="rc-card-sub">{c.desc}</div>
                    <div className="rc-card-stats">HP {c.hp} | MP {c.mp} | ATT {c.att} | DEF {c.def}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mage: Elemental Seed */}
            {needsSeed && (
              <div className="form-group">
                <label style={{ color: '#7eb8da', fontSize: 14, fontWeight: 600 }}>Starting Elemental Seed</label>
                <p style={{ color: '#999', fontSize: 12, margin: '2px 0 8px' }}>Choose the element you begin with. You will learn the associated starting spell.</p>
                <div className="rc-card-grid rc-card-grid-sm">
                  {ELEMENTAL_SEEDS.map(s => (
                    <div key={s.value} className={'rc-card rc-card-sm' + (seed === s.value ? ' rc-card-selected' : '')} onClick={() => setSeed(s.value)}>
                      <div className="rc-card-name">{s.label}</div>
                      <div className="rc-card-sub">Spell: {s.spell}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cleric: Deity */}
            {needsDeity && (
              <div className="form-group">
                <label style={{ color: '#e8c170', fontSize: 14, fontWeight: 600 }}>Choose Your Patron Deity</label>
                <p style={{ color: '#999', fontSize: 12, margin: '2px 0 8px' }}>Your deity determines your divine seeds and starting prayer.</p>
                <div className="rc-card-grid rc-card-grid-sm">
                  {DEITIES.map(d => (
                    <div key={d.value} className={'rc-card rc-card-sm' + (deity === d.value ? ' rc-card-selected' : '')} onClick={() => setDeity(d.value)}>
                      <div className="rc-card-name">{d.label}</div>
                      <div className="rc-card-sub">{d.title} ({d.align})</div>
                      <div className="rc-card-sub">Prayer: {d.spell}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Psion: Discipline */}
            {needsDiscipline && (
              <div className="form-group">
                <label style={{ color: '#c8a0e8', fontSize: 14, fontWeight: 600 }}>Choose Your Primary Discipline</label>
                <p style={{ color: '#999', fontSize: 12, margin: '2px 0 8px' }}>Your discipline determines your psionic focus stat and starting powers.</p>
                <div className="rc-card-grid rc-card-grid-sm">
                  {DISCIPLINES.map(d => (
                    <div key={d.value} className={'rc-card rc-card-sm' + (discipline === d.value ? ' rc-card-selected' : '')} onClick={() => setDiscipline(d.value)}>
                      <div className="rc-card-name">{d.label}</div>
                      <div className="rc-card-sub">{d.desc}</div>
                      <div className="rc-card-sub">Focus: {d.stat} | {d.spells}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Race */}
            <div className="form-group" style={{ marginTop: 12 }}>
              <label style={{ color: '#d4af37', fontSize: 14, fontWeight: 600 }}>Choose Your Race</label>
              <div className="rc-card-grid rc-card-grid-sm">
                {RACES.map(r => (
                  <div key={r.value} className={'rc-card rc-card-sm' + (race === r.value ? ' rc-card-selected' : '')} onClick={() => setRace(r.value)}>
                    <div className="rc-card-name">{r.label}</div>
                    <div className="rc-card-sub">{r.bonus}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Alignment */}
            <div className="form-group" style={{ marginTop: 12 }}>
              <label style={{ color: '#d4af37', fontSize: 14, fontWeight: 600 }}>Choose Your Alignment</label>
              <div className="rc-align-grid">
                {ALIGNMENTS.map(a => (
                  <div
                    key={a.value}
                    className={'rc-align-cell' + (alignment === a.value ? ' rc-align-selected' : '')}
                    onClick={() => setAlignment(a.value)}
                  >
                    {a.label}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn" onClick={() => setStep(1)} style={{ flex: 1, background: '#333', color: '#ccc' }}>Back</button>
              <button className="btn btn-primary" disabled={!canProceedStep2} onClick={() => setStep(3)} style={{ flex: 2 }}>Next</button>
            </div>
          </div>
        )}

        {/* STEP 3: Stats */}
        {step === 3 && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <h3 style={{ color: '#d4af37', marginBottom: 4, fontSize: 14 }}>
                Distribute 65 Attribute Points
              </h3>
              <p style={{ color: '#999', fontSize: 12, margin: 0 }}>
                Each stat: 6 to 18. Remaining: <strong style={{ color: remaining === 0 ? '#50c878' : remaining < 0 ? '#ff6b6b' : '#d4af37' }}>{remaining}</strong>
              </p>
              {raceInfo && (
                <p style={{ color: '#7eb8da', fontSize: 11, margin: '4px 0 0' }}>
                  {raceInfo.label} bonuses (shown live next to each stat): {raceInfo.bonus}
                </p>
              )}
            </div>

            <div className="rc-stat-grid">
              {Object.entries(stats).map(([key, val]) => {
                const bonus = (raceInfo && raceInfo.statBonus && raceInfo.statBonus[key]) || 0;
                const effective = val + bonus;
                return (
                  <div key={key} className="rc-stat-row">
                    <div className="rc-stat-label">
                      <span className="rc-stat-name">{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                      <span className="rc-stat-desc">{STAT_DESCRIPTIONS[key]}</span>
                    </div>
                    <div className="rc-stat-controls">
                      <button className="rc-stat-btn" onClick={() => setStat(key, val - 1)} disabled={val <= 6}>-</button>
                      <input
                        type="number"
                        className="rc-stat-input"
                        value={val}
                        onChange={e => setStat(key, e.target.value)}
                        min={6} max={18}
                      />
                      <button className="rc-stat-btn" onClick={() => setStat(key, val + 1)} disabled={val >= 18}>+</button>
                      {bonus !== 0 && (
                        <span style={{
                          marginLeft: 8,
                          fontSize: 11,
                          color: bonus > 0 ? '#7eb8da' : '#d77b7b',
                          whiteSpace: 'nowrap'
                        }}>
                          {bonus > 0 ? `+${bonus}` : bonus} = <strong style={{ color: '#d4af37' }}>{effective}</strong>
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            <div className="rc-summary">
              <div className="rc-summary-title">Character Summary</div>
              <div className="rc-summary-row"><span>Name:</span><strong>{characterName}</strong></div>
              <div className="rc-summary-row"><span>Class:</span><strong>{charClass}</strong></div>
              <div className="rc-summary-row"><span>Race:</span><strong>{race}</strong></div>
              <div className="rc-summary-row"><span>Alignment:</span><strong>{ALIGNMENTS.find(a => a.value === alignment)?.label}</strong></div>
              {needsSeed && <div className="rc-summary-row"><span>Element:</span><strong>{seed}</strong></div>}
              {needsDeity && <div className="rc-summary-row"><span>Deity:</span><strong>{deity}</strong></div>}
              {needsDiscipline && <div className="rc-summary-row"><span>Discipline:</span><strong>{DISCIPLINES.find(d => d.value === discipline)?.label}</strong></div>}
              {classInfo && (
                <div className="rc-summary-row"><span>Base:</span><strong>HP {classInfo.hp} | MP {classInfo.mp} | ATT {classInfo.att} | DEF {classInfo.def}</strong></div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn" onClick={() => setStep(2)} style={{ flex: 1, background: '#333', color: '#ccc' }}>Back</button>
              <button
                className="btn btn-primary"
                disabled={loading || !canSubmit}
                onClick={onSubmit}
                style={{ flex: 2 }}
              >
                {loading ? 'Creating...' : 'Create Character'}
              </button>
            </div>
          </div>
        )}

        {/* Login link */}
        <div className="text-center mt-2">
          <p style={{ color: '#ccc', marginTop: 12, fontSize: 13 }}>
            Already have a character?{' '}
            <Link to="/login" style={{ color: '#839dbf' }}>Login here</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
